import React from "react";
import { Paper, Typography, Box, Tabs, Tab, Stack, Accordion, AccordionSummary, AccordionDetails, alpha } from "@mui/material";
import { ExpandMore, LocationOn, HelpOutline, VideocamOutlined } from "@mui/icons-material";
import { IDelegate } from "../../../interfaces/IDelegate";
import { IDelegateRole } from "../../../interfaces/IDelegateRole";
import { IEventSessionType } from "../../../interfaces/IEventSessionType";
import LocationMapPicker from "../../Location/LocationMapPicker";
import EventSessionItem from "../EventSessionItem";

interface EventScheduleProps {
    groupedDays: Record<string, any>;
    selectedDay: number;
    handleDayChange: (event: React.SyntheticEvent, newValue: number) => void;
    expandedLocation: string | null;
    handleLocationAccordionChange: (locationId: string | undefined) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
    delegateRoles: IDelegateRole[];
    delegates: IDelegate[];
    onDelegateClick?: (delegate: IDelegate, roleId?: string) => void;
    eventTypes: IEventSessionType[];
    primaryColor?: string;
    secondaryColor?: string;
}

function TabPanel({ children, value, index }: { children?: React.ReactNode; value: number; index: number }) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`event-tabpanel-${index}`} aria-labelledby={`event-tab-${index}`}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export default function EventSchedule({
    groupedDays,
    selectedDay,
    handleDayChange,
    expandedLocation,
    handleLocationAccordionChange,
    delegateRoles,
    delegates,
    onDelegateClick,
    eventTypes,
    primaryColor = '#493193',
    secondaryColor = '#2fadd8',
}: EventScheduleProps) {
    // console.log('  groupedDays-->>:', groupedDays);
    //console.log('  groupedDays keys-->:', Object.keys(groupedDays));
    //console.log('  selectedDay index-->:', selectedDay);

    // Get the currently selected date
    //const dateKeys = Object.keys(groupedDays);
    //const selectedDate = dateKeys[selectedDay] || '';

    //console.log('  selectedDate:', selectedDate);

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, md: 4 },
                mb: { xs: 3, md: 4 },
                borderRadius: 3,
                border: `1px solid ${alpha(primaryColor, 0.1)}`,
                boxShadow: `0 4px 20px ${alpha(primaryColor, 0.06)}`,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.3rem", md: "1.5rem" },
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Event Schedule
                </Typography>
                {/*{selectedDate && (*/}
                {/*    <Typography*/}
                {/*        variant="body1"*/}
                {/*        sx={{ */}
                {/*            fontWeight: 600,*/}
                {/*            color: primaryColor,*/}
                {/*            backgroundColor: alpha(primaryColor, 0.08),*/}
                {/*            px: 2,*/}
                {/*            py: 0.75,*/}
                {/*            borderRadius: 2,*/}
                {/*            border: `1px solid ${alpha(primaryColor, 0.2)}`,*/}
                {/*        }}*/}
                {/*    >*/}
                {/*        📅 {selectedDate}*/}
                {/*    </Typography>*/}
                {/*)}*/}
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                    value={selectedDay}
                    onChange={handleDayChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTabs-indicator': {
                            backgroundColor: primaryColor,
                            height: 3,
                            borderRadius: '3px 3px 0 0',
                        },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: 'text.secondary',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                color: primaryColor,
                                bgcolor: alpha(primaryColor, 0.05),
                            },
                            '&.Mui-selected': {
                                color: primaryColor,
                            },
                        },
                    }}
                >
                    {Object.keys(groupedDays).map((date, index) => (
                        <Tab key={date} label={`Day ${index + 1}: ${date}`} id={`day-tab-${index}`} aria-controls={`day-tabpanel-${index}`} />
                    ))}
                </Tabs>
            </Box>
            {Object.entries(groupedDays).map(([date, days], index) => (
                <TabPanel key={date} value={selectedDay} index={index}>
                    <Stack spacing={3}>
                        {days.map((day: any) => {
                            const accordionKey = day.location?.id || day.id;
                            const isExpanded = expandedLocation === accordionKey;
                            // Defensive ordering in case the API response isn't already sorted
                            const sortedSessions = [...(day.sessions ?? [])].sort(
                                (a, b) => a.startTime.localeCompare(b.startTime)
                            );
                            return (
                                <Accordion
                                    key={accordionKey}
                                    expanded={isExpanded}
                                    onChange={handleLocationAccordionChange(accordionKey)}
                                    sx={{
                                        "&:before": { display: "none" },
                                        boxShadow: "none",
                                        backgroundColor: "transparent",
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        sx={{
                                            backgroundColor: alpha(primaryColor, 0.08),
                                            color: primaryColor,
                                            borderRadius: 2,
                                            border: `1px solid ${alpha(primaryColor, 0.2)}`,
                                            fontWeight: 600,
                                            transition: 'all 0.3s ease',
                                            "&.Mui-expanded": {
                                                borderBottomLeftRadius: 0,
                                                borderBottomRightRadius: 0,
                                                bgcolor: alpha(primaryColor, 0.12),
                                            },
                                            "&:hover": {
                                                bgcolor: alpha(primaryColor, 0.12),
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            {day.isTBD ? (
                                                <>
                                                    <HelpOutline sx={{ mr: 1, color: primaryColor }} />
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>
                                                        Location: To Be Determined
                                                        {day.tbdText && ` (${day.tbdText})`}
                                                        {" "}({sortedSessions.length} {sortedSessions.length === 1 ? "Session" : "Sessions"})
                                                    </Typography>
                                                </>
                                            ) : day.location ? (
                                                <>
                                                    <LocationOn sx={{ mr: 1, color: primaryColor }} />
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>
                                                        {day.location.name} ({sortedSessions.length} {sortedSessions.length === 1 ? "Session" : "Sessions"})
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <VideocamOutlined sx={{ mr: 1, color: primaryColor }} />
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>
                                                        Online ({sortedSessions.length} {sortedSessions.length === 1 ? "Session" : "Sessions"})
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 3 }}>
                                        <Stack spacing={2}>
                                            {!day.isTBD && day.location && isExpanded && (
                                                <LocationMapPicker latitude={day.location.latitude} longitude={day.location.longitude} readOnly={true} />
                                            )}
                                            {sortedSessions.length > 0 ? (
                                                sortedSessions.map((session: any) => (
                                                    <EventSessionItem
                                                        key={session.id}
                                                        dayId={day.id}
                                                        session={session}
                                                        location={day.isTBD ? undefined : day.location}
                                                        delegateRoles={delegateRoles}
                                                        delegates={delegates}
                                                        onDelegateClick={onDelegateClick}
                                                        eventTypes={eventTypes}
                                                        primaryColor={primaryColor}
                                                        secondaryColor={secondaryColor}
                                                    />
                                                ))
                                            ) : (
                                                <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    No sessions scheduled for this day.
                                                </Typography>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Stack>
                </TabPanel>
            ))}
        </Paper>
    );
}